import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Apple, UtensilsCrossed, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const FoodAndDrink = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/food-drink"]?.title} description={seoData["industries/food-drink"]?.description} path="/industries/food-drink" keywords={seoData["industries/food-drink"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Food & Beverage"
        description="FDA-compliant industrial coating contractor for food and beverage facilities. Hygienic floor coatings, tank linings, and equipment repair for processing plants, breweries, wineries, and cold storage."
        backgroundKey="food-drink"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Aggressive cleaning chemical attack on floors and walls',
              'Process tank corrosion and coating delamination',
              'Thermal cycling damage from hot washdowns and cold storage',
              'Slip hazards in wet processing areas',
              'Regulatory compliance for food contact surfaces',
              'Drain and trench deterioration from acidic and caustic washdowns'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-orange-50 rounded-lg">
                <Apple className="text-orange-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-orange-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <UtensilsCrossed className="text-orange-600 mr-3 flex-shrink-0" size={22} />
                  Hygienic Floor Coating Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">USDA/FDA compliant floor coating systems for processing areas, coolers, and loading docks. Antimicrobial, chemical-resistant, and thermal shock-resistant floor systems with integral cove bases and slip-resistant finishes.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-orange-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <UtensilsCrossed className="text-orange-600 mr-3 flex-shrink-0" size={22} />
                  Process Tank Lining
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Food-grade tank lining systems for fermentation tanks, mixing vessels, CIP tanks, and storage tanks. Coatings certified for direct food and beverage contact.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-orange-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <UtensilsCrossed className="text-orange-600 mr-3 flex-shrink-0" size={22} />
                  Cold Storage & Freezer Coatings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Thermal shock-resistant floor and wall coatings for cold rooms, blast freezers, and refrigerated warehouses. Systems designed for continuous freeze-thaw cycling.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-orange-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <UtensilsCrossed className="text-orange-600 mr-3 flex-shrink-0" size={22} />
                  Drain & Secondary Containment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Chemical-resistant drain channel, trench, and secondary containment coatings for processing areas. Seamless, hygienic surfaces that are easy to clean and sanitize.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Food & Beverage?</h2>
              <div className="space-y-3">
                {['USDA/FDA compliant coating systems for food contact',
                  'Antimicrobial and hygienic surface finishes',
                  'Thermal shock resistance for hot washdown and cold storage',
                  'Slip-resistant finishes for wet processing environments',
                  'Rapid cure for minimal production disruption',
                  'Full regulatory documentation and compliance support'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-orange-600 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Food Safety Certified</h3>
              <p className="text-orange-100 mb-6">BCS installs USDA and FDA compliant coating systems trusted by food processors, breweries, and cold storage facilities across California.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/floor-repair')}>
                View Floor Coatings <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Food Processing Facility</h2>
          <p className="text-blue-100 mb-8">Contact us for a hygienic coating assessment for your food or beverage plant.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FoodAndDrink;
